import React, { useState, useEffect } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import queryString from 'query-string';
import { checkIsAdminLoggined } from '../../../../utils/utils.js';
import { paymentsApi, bankAccountsApi } from '../../../../api/bankApi';
import PaymentAttachmentModal from '../payment/PaymentAttachmentModal';
import { Helmet } from 'react-helmet';
import ReactPaginate from 'react-paginate';
import Footer from '../../../Footer';
import Header from '../../../Header';
import privatbankIcon from '../../../../assets/images/privatbank.png';
import monobankIcon from '../../../../assets/images/monobank.png';

const STATUS_LABELS = {
  PENDING: 'Нова',
  PARTIALLY_USED: 'Частково використана',
  USED: 'Використана',
  ARCHIVED: 'Архівована',
};

/** Парсить фільтри з location.search; для status використовує URLSearchParams.getAll, щоб коректно отримати масив. */
function getFiltersFromSearch(search) {
  const params = new URLSearchParams(search.replace(/^\?/, ''));
  const page = params.get('page');
  const status = params.getAll('status');
  return {
    page: page ? parseInt(page, 10) - 1 : 0,
    statusFilter: status || [],
    typeFilter: params.get('type') || '',
    bankAccountIdFilter: params.get('bankAccountId') || '',
    dateFromFilter: (params.get('dateFrom') || '').split('T')[0] || '',
    dateToFilter: (params.get('dateTo') || '').split('T')[0] || '',
    searchQuery: params.get('searchQuery') || '',
  };
}

const AdminPaymentsPageMobile = (props) => {
  const token = localStorage.getItem('token');
  const history = useHistory();
  const location = useLocation();
  const initialFilters = getFiltersFromSearch(location.search);

  const [payments, setPayments] = useState([]);
  const [bankAccounts, setBankAccounts] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(initialFilters.page);
  const numberPerPage = 20;
  const [totalNumber, setTotalNumber] = useState(0);
  const [pageCount, setPageCount] = useState(0);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [statusDropdownOpen, setStatusDropdownOpen] = useState(false);

  const [bankAccountIdFilter, setBankAccountIdFilter] = useState(initialFilters.bankAccountIdFilter);
  const [typeFilter, setTypeFilter] = useState(initialFilters.typeFilter);
  const [statusFilter, setStatusFilter] = useState(initialFilters.statusFilter);
  const [dateFromFilter, setDateFromFilter] = useState(initialFilters.dateFromFilter);
  const [dateToFilter, setDateToFilter] = useState(initialFilters.dateToFilter);
  const [searchQuery, setSearchQuery] = useState(initialFilters.searchQuery);

  const [commentModalPayment, setCommentModalPayment] = useState(null);
  const [commentText, setCommentText] = useState('');
  const [fullCommentModal, setFullCommentModal] = useState('');
  const [archiveConfirmPayment, setArchiveConfirmPayment] = useState(null);
  const [unarchiveConfirmPayment, setUnarchiveConfirmPayment] = useState(null);
  const [manualModalOpen, setManualModalOpen] = useState(false);
  const [manualForm, setManualForm] = useState({ amount: '', type: 'IN', adminComment: '' });
  const [manualError, setManualError] = useState('');

  const [attachModalPayment, setAttachModalPayment] = useState(null);
  useEffect(() => {
    checkIsAdminLoggined(props);
    window.scrollTo(0, 0);
    bankAccountsApi.getAll()
      .then(res => res.status === 200 && res.data.data && setBankAccounts(res.data.data))
      .catch(err => console.error(err));
  }, []);

  useEffect(() => {
    if (!statusDropdownOpen) return;
    const close = (e) => {
      if (!e.target.closest('.status-dropdown-mobile')) setStatusDropdownOpen(false);
    };
    document.addEventListener('click', close);
    return () => document.removeEventListener('click', close);
  }, [statusDropdownOpen]);

  const loadPayments = async (page) => {
    setIsLoading(true);
    const params = { page: (page ?? currentPage) + 1, numberPerPage };
    if (bankAccountIdFilter) params.bankAccountId = bankAccountIdFilter;
    if (typeFilter) params.type = typeFilter;
    if (statusFilter.length) params.status = statusFilter;
    if (dateFromFilter) params.dateFrom = dateFromFilter + 'T00:00:00';
    if (dateToFilter) params.dateTo = dateToFilter + 'T23:59:59';
    if (searchQuery) params.searchQuery = searchQuery;
    try {
      const res = await paymentsApi.getAll(params);
      if (res.status === 200 && res.data.data) {
        setPayments(res.data.data);
        setTotalNumber(res.data.totalNumber || 0);
        setPageCount(Math.ceil((res.data.totalNumber || 0) / numberPerPage));
      }
    } catch (e) {
      console.error(e);
    } finally {
      setIsLoading(false);
    }
  };

  const refreshAttachedPayment = async paymentId => {
    const response = await paymentsApi.getById(paymentId);
    const updated = response.data.data;
    if (!updated || Number(updated.id) !== Number(paymentId)) {
      throw new Error('Не вдалося оновити оплату');
    }
    setPayments(previous => previous.map(payment => payment.id === updated.id ? { ...payment, ...updated } : payment));
  };

  const updateURL = (page = currentPage) => {
    const params = {};
    if (bankAccountIdFilter) params.bankAccountId = bankAccountIdFilter;
    if (typeFilter) params.type = typeFilter;
    if (statusFilter.length) params.status = statusFilter;
    if (dateFromFilter) params.dateFrom = dateFromFilter;
    if (dateToFilter) params.dateTo = dateToFilter;
    if (searchQuery) params.searchQuery = searchQuery;
    if (page >= 0) params.page = page + 1;
    const query = queryString.stringify(params);
    const newUrl = `/admin/payments${query ? `?${query}` : ''}`;
    if (location.pathname + location.search !== newUrl) history.push(newUrl);
  };

  const statusArraysEqual = (a, b) => {
    if (!a && !b) return true;
    if (!a || !b || a.length !== b.length) return false;
    const sa = [...a].sort();
    const sb = [...b].sort();
    return sa.every((v, i) => v === sb[i]);
  };

  useEffect(() => {
    loadPayments(currentPage);
  }, [currentPage, bankAccountIdFilter, typeFilter, statusFilter, dateFromFilter, dateToFilter, searchQuery]);

  useEffect(() => {
    const fromUrl = getFiltersFromSearch(location.search);
    const urlMatchesState =
      fromUrl.page === currentPage &&
      fromUrl.bankAccountIdFilter === bankAccountIdFilter &&
      fromUrl.typeFilter === typeFilter &&
      statusArraysEqual(fromUrl.statusFilter, statusFilter) &&
      fromUrl.dateFromFilter === dateFromFilter &&
      fromUrl.dateToFilter === dateToFilter &&
      fromUrl.searchQuery === searchQuery;
    if (urlMatchesState) return;
    if (fromUrl.page !== currentPage) setCurrentPage(fromUrl.page);
    if (fromUrl.bankAccountIdFilter !== bankAccountIdFilter) setBankAccountIdFilter(fromUrl.bankAccountIdFilter);
    if (fromUrl.typeFilter !== typeFilter) setTypeFilter(fromUrl.typeFilter);
    if (!statusArraysEqual(fromUrl.statusFilter, statusFilter)) setStatusFilter(fromUrl.statusFilter);
    if (fromUrl.dateFromFilter !== dateFromFilter) setDateFromFilter(fromUrl.dateFromFilter);
    if (fromUrl.dateToFilter !== dateToFilter) setDateToFilter(fromUrl.dateToFilter);
    if (fromUrl.searchQuery !== searchQuery) setSearchQuery(fromUrl.searchQuery);
  }, [location.search]);

  useEffect(() => {
    updateURL(currentPage);
  }, [currentPage, bankAccountIdFilter, typeFilter, statusFilter, dateFromFilter, dateToFilter, searchQuery]);

  const handlePageClick = (data) => {
    const page = data.selected;
    setCurrentPage(page);
    updateURL(page);
    window.scrollTo(0, 0);
  };

  const handleSearch = () => {
    setCurrentPage(0);
    updateURL(0);
    loadPayments(0);
  };

  const handleResetFilters = () => {
    setBankAccountIdFilter('');
    setTypeFilter('');
    setStatusFilter([]);
    setDateFromFilter('');
    setDateToFilter('');
    setSearchQuery('');
    setCurrentPage(0);
    history.push('/admin/payments');
    setTimeout(() => loadPayments(0), 100);
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    const d = new Date(dateString);
    if (Number.isNaN(d.getTime())) return String(dateString);
    return d.toLocaleDateString('uk-UA', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  };

  const formatPaymentDate = (createdAt, transactionDate) => {
    const v = createdAt || transactionDate;
    if (!v) return '-';
    return formatDate(v);
  };

  const formatCurrency = (amount, withCurrency = true) => {
    if (amount == null) return withCurrency ? '0 грн' : '0';
    const n = parseFloat(amount).toLocaleString('uk-UA', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    return withCurrency ? `${n} грн` : n;
  };

  const toggleStatus = (status) => {
    if (statusFilter.includes(status)) setStatusFilter(statusFilter.filter(s => s !== status));
    else setStatusFilter([...statusFilter, status]);
  };

  const handleSaveComment = async () => {
    if (!commentModalPayment) return;
    try {
      await paymentsApi.updateComment(commentModalPayment.id, commentText);
      setPayments(prev => prev.map(p => p.id === commentModalPayment.id ? { ...p, adminComment: commentText } : p));
      setCommentModalPayment(null);
      setCommentText('');
    } catch (e) {
      alert(e.response?.data?.data || 'Помилка збереження коментаря');
    }
  };

  const handleArchive = async () => {
    if (!archiveConfirmPayment) return;
    try {
      await paymentsApi.updateStatus(archiveConfirmPayment.id, 'ARCHIVED');
      setPayments(prev => prev.map(p => p.id === archiveConfirmPayment.id ? { ...p, status: { ...p.status, databaseValue: 'ARCHIVED', displayName: 'Архівована' } } : p));
      setArchiveConfirmPayment(null);
    } catch (e) {
      alert(e.response?.data?.data || 'Помилка архівування');
    }
  };

  const handleUnarchive = async () => {
    if (!unarchiveConfirmPayment) return;
    try {
      await paymentsApi.updateStatus(unarchiveConfirmPayment.id, 'PENDING');
      setPayments(prev => prev.map(p => p.id === unarchiveConfirmPayment.id ? { ...p, status: { ...p.status, databaseValue: 'PENDING', displayName: 'Нова' } } : p));
      setUnarchiveConfirmPayment(null);
    } catch (e) {
      alert(e.response?.data?.data || 'Помилка повернення з архіву');
    }
  };

  const handleCreateManual = async () => {
    setManualError('');
    const amount = parseFloat(manualForm.amount);
    if (!manualForm.amount || isNaN(amount) || amount <= 0) {
      setManualError('Сума має бути більше 0');
      return;
    }
    try {
      await paymentsApi.createManual({
        amount,
        type: manualForm.type,
        adminComment: manualForm.adminComment || null,
      });
      setManualModalOpen(false);
      setManualForm({ amount: '', type: 'IN', adminComment: '' });
      loadPayments(currentPage);
    } catch (e) {
      setManualError(e.response?.data?.data || 'Помилка створення');
    }
  };

  return (
    <div className="container px-2">
      <style>{`
        .status-filter-cb {
          -webkit-appearance: none;
          appearance: none;
          width: 22px;
          height: 22px;
          border: 2px solid #6c757d;
          border-radius: 4px;
          cursor: pointer;
          flex-shrink: 0;
          margin: 0 10px 0 0;
          background-color: #fff;
        }
        .status-filter-cb:checked {
          background-color: #007bff;
          border-color: #007bff;
          background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 16 16'%3E%3Cpath fill='none' stroke='white' stroke-width='2.5' stroke-linecap='round' stroke-linejoin='round' d='M2 8l4 4 8-8'/%3E%3C/svg%3E");
        }
      `}</style>
      <Helmet><title>Адмін панель - Оплати (мобільна версія)</title></Helmet>
      <div className="row">
        <div className="col p-0">
          <Header />
        </div>
      </div>

      <div className="row mt-2">
        <div className="col-12">
          <h5 className="font-weight-bold mb-2">Оплати</h5>

          <div className="mb-2">
            <button
              type="button"
              className="btn btn-outline-secondary btn-sm btn-block d-flex align-items-center justify-content-between"
              onClick={() => setFiltersOpen(!filtersOpen)}
            >
              <span>Фільтри</span>
              <i className={`bi bi-chevron-${filtersOpen ? 'up' : 'down'}`} />
            </button>
            {filtersOpen && (
              <div className="border rounded p-2 mt-1 bg-light">
                <div className="form-group mb-2">
                  <label className="small font-weight-bold mb-0">Рахунок</label>
                  <select
                    className="form-control form-control-sm"
                    value={bankAccountIdFilter}
                    onChange={e => setBankAccountIdFilter(e.target.value)}
                  >
                    <option value="">Всі</option>
                    {bankAccounts.map(a => (
                      <option key={a.id} value={a.id}>{a.name}</option>
                    ))}
                  </select>
                </div>
                <div className="form-group mb-2">
                  <label className="small font-weight-bold mb-0">Тип</label>
                  <select className="form-control form-control-sm" value={typeFilter} onChange={e => setTypeFilter(e.target.value)}>
                    <option value="">Всі</option>
                    <option value="IN">Вхідні</option>
                    <option value="OUT">Вихідні</option>
                  </select>
                </div>
                <div className="form-group mb-2 position-relative status-dropdown-mobile">
                  <label className="small font-weight-bold mb-0">Статус</label>
                  <button
                    type="button"
                    className="form-control form-control-sm text-left d-flex align-items-center justify-content-between bg-white border"
                    style={{ minHeight: '31px' }}
                    onClick={() => setStatusDropdownOpen(!statusDropdownOpen)}
                  >
                    <span className="text-truncate">
                      {statusFilter.length === 0
                        ? 'Всі'
                        : statusFilter.length === 1
                          ? STATUS_LABELS[statusFilter[0]]
                          : `Обрано: ${statusFilter.length}`}
                    </span>
                    <i className={`bi bi-chevron-${statusDropdownOpen ? 'up' : 'down'} ml-1 flex-shrink-0`} />
                  </button>
                  {statusDropdownOpen && (
                    <div
                      className="border rounded bg-white shadow-sm position-absolute w-100 mt-1 p-2"
                      style={{ zIndex: 1050, maxHeight: 220, overflowY: 'auto' }}
                    >
                      {['PENDING', 'PARTIALLY_USED', 'USED', 'ARCHIVED'].map(s => (
                        <div
                          key={s}
                          className="d-flex align-items-center py-2"
                          onClick={() => toggleStatus(s)}
                          style={{ cursor: 'pointer' }}
                        >
                          <input
                            type="checkbox"
                            className="status-filter-cb"
                            id={`st-${s}`}
                            checked={statusFilter.includes(s)}
                            onChange={() => toggleStatus(s)}
                            onClick={e => e.stopPropagation()}
                          />
                          <label className="mb-0 flex-grow-1" htmlFor={`st-${s}`} style={{ cursor: 'pointer', fontSize: '0.9375rem' }}>
                            {STATUS_LABELS[s]}
                          </label>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                <div className="row">
                  <div className="col-6 form-group mb-2">
                    <label className="small font-weight-bold mb-0">Від</label>
                    <input type="date" className="form-control form-control-sm" value={dateFromFilter} onChange={e => setDateFromFilter(e.target.value)} />
                  </div>
                  <div className="col-6 form-group mb-2">
                    <label className="small font-weight-bold mb-0">До</label>
                    <input type="date" className="form-control form-control-sm" value={dateToFilter} onChange={e => setDateToFilter(e.target.value)} />
                  </div>
                </div>
                <div className="form-group mb-2">
                  <label className="small font-weight-bold mb-0">Пошук</label>
                  <input
                    type="text"
                    className="form-control form-control-sm"
                    placeholder="Опис, контрагент..."
                    value={searchQuery}
                    onChange={e => setSearchQuery(e.target.value)}
                  />
                </div>
                <div className="d-flex flex-wrap justify-content-end" style={{ gap: '0.5rem' }}>
                  <button className="btn btn-danger btn-sm" onClick={handleResetFilters}>Очистити</button>
                  <button className="btn btn-dark btn-sm" onClick={handleSearch}>Оновити</button>
                </div>
              </div>
            )}
          </div>

          <div className="d-flex justify-content-between align-items-center mb-2">
            <span className="text-dark">Всього: {totalNumber}</span>
            <button className="btn btn-success btn-sm" onClick={() => { setManualModalOpen(true); setManualError(''); }}>
              <i className="bi bi-plus-circle mr-1" /> Додати вручну
            </button>
          </div>

          {isLoading ? (
            <div className="text-center py-5">
              <div className="spinner-border text-secondary" role="status"><span className="sr-only">Завантаження...</span></div>
            </div>
          ) : payments.length === 0 ? (
            <p className="text-center text-muted py-4">Оплат не знайдено</p>
          ) : (
            <>
              <div className="list-group list-group-flush">
                {payments.map(payment => {
                  const typeVal = payment.type?.databaseValue || payment.type?.name || payment.type;
                  const isIn = typeVal === 'IN' || typeVal === 'INCOMING';
                  const statusVal = payment.status?.databaseValue || '';
                  return (
                    <div key={payment.id} className="card mb-2 shadow-sm">
                      <div className="card-body p-3">
                        <div className="d-flex justify-content-between align-items-start mb-1">
                          <span className="text-muted small">#{payment.id}</span>
                          <span className="small">{formatPaymentDate(payment.transactionDate, payment.createdAt)}</span>
                        </div>
                        <div className="d-flex align-items-center flex-wrap mb-1">
                          {!payment.isManual && payment.bankType?.databaseValue === 'PRIVATBANK' && (
                            <img src={privatbankIcon} alt="" style={{ width: 18, height: 18, marginRight: 6 }} />
                          )}
                          {!payment.isManual && payment.bankType?.databaseValue === 'MONOBANK' && (
                            <img src={monobankIcon} alt="" style={{ width: 18, height: 18, marginRight: 6 }} />
                          )}
                          <span className="font-weight-bold">{payment.isManual ? 'Ручна оплата' : (payment.bankAccountName || payment.bankAccountId || '-')}</span>
                          <span className={`badge badge-${statusVal === 'USED' ? 'success' : statusVal === 'ARCHIVED' ? 'dark' : statusVal === 'PARTIALLY_USED' ? 'warning' : 'secondary'} ml-2`}>
                            {payment.status?.displayName || STATUS_LABELS[statusVal] || statusVal}
                          </span>
                        </div>
                        <div className={`font-weight-bold ${isIn ? 'text-success' : 'text-danger'}`} style={{ fontSize: '1.1rem' }}>
                          {isIn ? '+' : '-'}{formatCurrency(payment.amount)}
                          {payment.status?.databaseValue === 'PARTIALLY_USED' && payment.availableAmount != null && (
                            <span className="small font-weight-normal text-muted ml-1">
                              (залиш. {formatCurrency(payment.availableAmount)})
                            </span>
                          )}
                        </div>
                        {(payment.bankDescription || payment.counterparty) && (
                          <div className="small text-muted mt-1" style={{ wordBreak: 'break-word' }}>
                            {payment.bankDescription}
                            {payment.bankDescription && payment.counterparty && ' '}
                            {payment.counterparty}
                          </div>
                        )}
                        {payment.attachedOrderIds && payment.attachedOrderIds.length > 0 && (
                          <div className="mt-1">
                            {payment.attachedOrderIds.map(oid => (
                              <a key={oid} href={`/admin/order/${oid}`} target="_blank" rel="noopener noreferrer" className="small mr-2">
                                Замовлення #{oid}
                              </a>
                            ))}
                          </div>
                        )}
                        {payment.adminComment && (
                          <div
                            className="small text-muted mt-1"
                            style={{ cursor: 'pointer', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                            onClick={() => setFullCommentModal(payment.adminComment)}
                            title={payment.adminComment}
                          >
                            <i className="bi bi-chat-left-text mr-1" />
                            {payment.adminComment.length > 40 ? payment.adminComment.substring(0, 40) + '...' : payment.adminComment}
                          </div>
                        )}
                        <div className="d-flex flex-wrap mt-2 pt-2 border-top">
                          <button
                            type="button"
                            className="btn btn-outline-secondary btn-sm mr-1 mb-1"
                            onClick={() => { setCommentModalPayment(payment); setCommentText(payment.adminComment || ''); }}
                          >
                            <i className="bi bi-chat-left-text" /> Коментар
                          </button>
                          {(statusVal === 'PENDING' || statusVal === 'PARTIALLY_USED') && (
                            <button type="button" className="btn btn-outline-secondary btn-sm mr-1 mb-1" onClick={() => setArchiveConfirmPayment(payment)}>
                              <i className="bi bi-trash" /> В архів
                            </button>
                          )}
                          {statusVal === 'ARCHIVED' && (
                            <button type="button" className="btn btn-outline-secondary btn-sm mr-1 mb-1" onClick={() => setUnarchiveConfirmPayment(payment)}>
                              <i className="bi bi-arrow-counterclockwise" /> З архіву
                            </button>
                          )}
                          <button
                            type="button"
                            className="btn btn-outline-dark btn-sm mb-1"
                            onClick={() => setAttachModalPayment(payment)}
                            aria-label={statusVal === 'USED' ? 'Прикріплені замовлення' : 'Прикріпити до замовлення'}
                          >
                            <i className="bi bi-link-45deg" /> {statusVal === 'USED' ? 'Прикріплені замовлення' : 'Прикріпити до замовлення'}
                          </button>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>

              {pageCount > 1 && (
                <div className="d-flex justify-content-center mt-3">
                  <ReactPaginate
                    previousLabel="<"
                    nextLabel=">"
                    pageCount={pageCount}
                    onPageChange={handlePageClick}
                    marginPagesDisplayed={1}
                    pageRangeDisplayed={3}
                    containerClassName="pagination pagination-sm"
                    activeClassName="active"
                    forcePage={currentPage}
                  />
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* Модаль: коментар */}
      {commentModalPayment && (
        <div className="modal fade show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }} tabIndex="-1" onClick={() => setCommentModalPayment(null)}>
          <div className="modal-dialog modal-dialog-centered" onClick={e => e.stopPropagation()}>
            <div className="modal-content">
              <div className="modal-header">
                <h6 className="modal-title">Коментар (оплата #{commentModalPayment.id})</h6>
                <button type="button" className="close" onClick={() => setCommentModalPayment(null)}><span>&times;</span></button>
              </div>
              <div className="modal-body">
                <textarea
                  className="form-control"
                  rows={3}
                  value={commentText}
                  onChange={e => setCommentText(e.target.value)}
                  placeholder="Коментар адміна"
                />
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary btn-sm" onClick={() => setCommentModalPayment(null)}>Скасувати</button>
                <button type="button" className="btn btn-dark btn-sm" onClick={handleSaveComment}>Зберегти</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Модаль: повний коментар */}
      {fullCommentModal !== '' && (
        <div className="modal fade show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }} tabIndex="-1" onClick={() => setFullCommentModal('')}>
          <div className="modal-dialog modal-dialog-centered" onClick={e => e.stopPropagation()}>
            <div className="modal-content">
              <div className="modal-header">
                <h6 className="modal-title">Коментар</h6>
                <button type="button" className="close" onClick={() => setFullCommentModal('')}><span>&times;</span></button>
              </div>
              <div className="modal-body"><p style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', marginBottom: 0 }}>{fullCommentModal}</p></div>
            </div>
          </div>
        </div>
      )}

      {/* Модаль: архівувати */}
      {archiveConfirmPayment && (
        <div className="modal fade show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }} tabIndex="-1" onClick={() => setArchiveConfirmPayment(null)}>
          <div className="modal-dialog modal-dialog-centered" onClick={e => e.stopPropagation()}>
            <div className="modal-content">
              <div className="modal-body">Архівувати оплату #{archiveConfirmPayment.id}?</div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary btn-sm" onClick={() => setArchiveConfirmPayment(null)}>Ні</button>
                <button type="button" className="btn btn-dark btn-sm" onClick={handleArchive}>Так</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Модаль: з архіву */}
      {unarchiveConfirmPayment && (
        <div className="modal fade show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }} tabIndex="-1" onClick={() => setUnarchiveConfirmPayment(null)}>
          <div className="modal-dialog modal-dialog-centered" onClick={e => e.stopPropagation()}>
            <div className="modal-content">
              <div className="modal-body">Повернути оплату #{unarchiveConfirmPayment.id} з архіву?</div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary btn-sm" onClick={() => setUnarchiveConfirmPayment(null)}>Ні</button>
                <button type="button" className="btn btn-dark btn-sm" onClick={handleUnarchive}>Так</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Модаль: прикріпити до замовлення */}
      <PaymentAttachmentModal payment={attachModalPayment}
        onClose={() => setAttachModalPayment(null)} onAttached={refreshAttachedPayment} />

      {/* Модаль: ручна оплата */}
      {manualModalOpen && (
        <div className="modal fade show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }} tabIndex="-1" onClick={() => setManualModalOpen(false)}>
          <div className="modal-dialog modal-dialog-centered" onClick={e => e.stopPropagation()}>
            <div className="modal-content">
              <div className="modal-header">
                <h6 className="modal-title">Додати оплату вручну</h6>
                <button type="button" className="close" onClick={() => setManualModalOpen(false)}><span>&times;</span></button>
              </div>
              <div className="modal-body">
                {manualError && <div className="alert alert-danger small py-2">{manualError}</div>}
                <div className="form-group">
                  <label className="small font-weight-bold">Сума (грн)</label>
                  <input type="number" step="0.01" min="0.01" className="form-control" value={manualForm.amount} onChange={e => setManualForm(prev => ({ ...prev, amount: e.target.value }))} />
                </div>
                <div className="form-group">
                  <label className="small font-weight-bold">Тип</label>
                  <select className="form-control" value={manualForm.type} onChange={e => setManualForm(prev => ({ ...prev, type: e.target.value }))}>
                    <option value="IN">Вхід</option>
                    <option value="OUT">Вихід</option>
                  </select>
                </div>
                <div className="form-group">
                  <label className="small font-weight-bold">Коментар</label>
                  <input type="text" className="form-control" value={manualForm.adminComment} onChange={e => setManualForm(prev => ({ ...prev, adminComment: e.target.value }))} placeholder="Необов'язково" />
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary btn-sm" onClick={() => setManualModalOpen(false)}>Скасувати</button>
                <button type="button" className="btn btn-success btn-sm" onClick={handleCreateManual}>Створити</button>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="row mt-3">
        <div className="col p-0">
          <Footer />
        </div>
      </div>
    </div>
  );
};

export default AdminPaymentsPageMobile;
